import { PropsWithChildren } from "react";
import "./App.css";

import { BrowserRouter as Router, Switch, Route, Redirect, RouteProps } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { createTheme } from "@mui/material/styles";
import { createAppTheme, FullPageLayout } from "@mb-pro-ui/components";

import { AuthProvider, useAuth, UserData } from "./utils/AuthProvider";
import { UserInfoProvider } from "./utils/user-info";
import { ApiProvider } from "./utils/ApiProvider";
import { LoginPage, DevicesPage, DevicePage } from "./pages";

import AppHeader from "./components/header/AppHeader";

import LocaleProvider from "./locales/LocaleProvider";
import { DevicesProvider } from "./utils/DevicesProvider";
import { MenuButtonContainerProvider } from "./components/header/MenuButtonContainerProvider";
import UserSettings from "./components/UserSettings";
import RemotefilesPage from "./pages/RemoteFilesPage";
import RemoteFilePage from "./pages/RemoteFilePage";
import FirmwarePage from "./pages/FirmwarePage";
import FirmwaresPage from "./pages/FirmwaresPage";
import TemplateHelperPage from "./pages/TemplateHelperPage";
import TemplateHelpersPage from "./pages/TemplateHelpersPage";
import FirmwareUpdatePage from "./pages/FirmwareUpdatePage";
import UsersPage from "./pages/UsersPage";
import UserPage from "./pages/UserPage";
import { PasswordResetPage } from "./pages/PasswordResetPage";
import { ConfigImportPage } from "./pages/ConfigImportPage";

// const API = "https://helios-api-dev.mohanet.com/toolbox";
const API = "https://helios-api.mohanet.com/toolbox";
// const API = "http://localhost:4000/toolbox";

enum UserRight {
  FIRMWARE_UPDATE = "firmware_update",
  FIRMWARE_UPLOAD = "firmware_upload",
  REMOTE_FILES = "remote_files",
  GLOBAL_REMOTE_FILES = "global_remote_files",
  USER_ADMIN = "user_admin",
  TEMPLATE_HELPER = "template_helper",
  TEMPLATE_EDIT = "template_edit",
}
type PrivateRouteProps = RouteProps & {
  userRight?: UserRight | UserRight[];
};

export const APP_NAME = "MOHAnet Toolbox";

const PrivateRoute = ({ children, userRight, ...rest }: PrivateRouteProps) => {
  const auth = useAuth();
  const rights = userRight === undefined ? [] : Array.isArray(userRight) ? userRight : [userRight];
  const hasRight = auth.user && rights.every((r) => (auth.user as UserData).rights?.[r]);
  return (
    <Route
      {...rest}
      render={({ location }) =>
        auth.user ? (
          hasRight ? (
            children
          ) : (
            <Redirect
              to={{
                pathname: "/",
              }}
            />
          )
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

const baseTheme = createTheme({
  palette: {
    common: {
      white: "#FEFEFE",
    },
    primary: {
      main: "#294f6b",
      contrastText: "#FEFEFE",
    },
    secondary: {
      main: "#4a86ba",
    },
    warning: {
      main: "#EF9F16",
      contrastText: "#FEFEFE",
    },
    divider: "#D4D4D4",
    text: {
      primary: "#515151",
      // secondary: "#795548",
    },
    background: {
      paper: "#FEFEFE",
      default: "#FAFAFA",
    },
    // header: {
    //   main: "#795548",
    //   dark: "#795548",
    //   light: "#795548",
    //   // dark: "#143675",
    //   // light: "#4b5fa4",
    // },
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        regular: {
          minHeight: "66px",
          "@media (min-width: 600px)": {
            minHeight: "72px",
          },
        },
      },
    },
  },
});

const toolboxTheme = createAppTheme(baseTheme);

const Providers = ({ children }: PropsWithChildren<{}>) => {
  return (
    <ThemeProvider theme={toolboxTheme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={5} preventDuplicate={true}>
        <UserInfoProvider>
          <ApiProvider baseUrl={API}>
            <AuthProvider>
              <LocaleProvider>
                <DevicesProvider>{children}</DevicesProvider>
              </LocaleProvider>
            </AuthProvider>
          </ApiProvider>
        </UserInfoProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

const AppContent = () => {
  return (
    <>
      <Switch>
        <Route path="/login">
          <LoginPage />
        </Route>
        <Route path="/password-reset/:id">
          <PasswordResetPage />
        </Route>
        <Route path="/password-reset">
          <PasswordResetPage />
        </Route>
        <PrivateRoute>
          <FullPageLayout>
            <MenuButtonContainerProvider>
              <AppHeader />
              <Switch>
                <PrivateRoute path="/settings">
                  <UserSettings />
                </PrivateRoute>
                <PrivateRoute path="/configuration/:unitid">
                  <DevicePage />
                </PrivateRoute>
                <PrivateRoute path="/configuration">
                  <DevicesPage />
                </PrivateRoute>
                <PrivateRoute path="/configuration-import">
                  <ConfigImportPage />
                </PrivateRoute>
                <PrivateRoute path="/firmware-update" userRight={UserRight.FIRMWARE_UPDATE}>
                  <FirmwareUpdatePage />
                </PrivateRoute>
                <PrivateRoute path="/firmwares/:id" userRight={UserRight.FIRMWARE_UPLOAD}>
                  <FirmwarePage />
                </PrivateRoute>
                <PrivateRoute path="/firmwares" userRight={UserRight.FIRMWARE_UPLOAD}>
                  <FirmwaresPage />
                </PrivateRoute>
                <PrivateRoute path="/files/:id" userRight={UserRight.REMOTE_FILES}>
                  <RemoteFilePage />
                </PrivateRoute>
                <PrivateRoute path="/files" userRight={UserRight.REMOTE_FILES}>
                  <RemotefilesPage />
                </PrivateRoute>
                <PrivateRoute path="/template-helpers/:id" userRight={UserRight.TEMPLATE_HELPER}>
                  <TemplateHelperPage />
                </PrivateRoute>
                <PrivateRoute path="/template-helpers" userRight={UserRight.TEMPLATE_HELPER}>
                  <TemplateHelpersPage />
                </PrivateRoute>
                <PrivateRoute path="/users/:id" userRight={UserRight.USER_ADMIN}>
                  <UserPage />
                </PrivateRoute>
                <PrivateRoute path="/users" userRight={UserRight.USER_ADMIN}>
                  <UsersPage />
                </PrivateRoute>

                <Route>
                  <Redirect to="/configuration" />
                </Route>
              </Switch>
            </MenuButtonContainerProvider>
          </FullPageLayout>
        </PrivateRoute>
      </Switch>
    </>
  );
};

export default function App() {
  return (
    <Providers>
      <Router basename="/">
        <AppContent />
      </Router>
    </Providers>
  );
}
